import {
  Component, Input, OnInit, OnDestroy, ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CustomFlexLayoutModule } from 'src/app/shared/flex-layout/custom-flex-layout.module';
import { LeadActivityService } from 'src/app/services/lead-activity.service';

/** Activity type icon and colour mapping */
const ACTIVITY_CONFIG: Record<string, { icon: string; color: string }> = {
  NOTE_CREATED:      { icon: 'note_add',          color: '#3f51b5' },
  NOTE_REPLIED:      { icon: 'chat_bubble',        color: '#7986cb' },
  NOTE_MENTIONED:    { icon: 'alternate_email',    color: '#5c6bc0' },
  TASK_CREATED:      { icon: 'add_task',           color: '#00897b' },
  TASK_UPDATED:      { icon: 'edit_note',          color: '#26a69a' },
  TASK_ASSIGNED:     { icon: 'assignment_ind',     color: '#26a69a' },
  TASK_COMPLETED:    { icon: 'task_alt',           color: '#43a047' },
  TASK_OVERDUE:      { icon: 'warning',            color: '#e53935' },
  LEAD_DELEGATED:    { icon: 'swap_horiz',         color: '#fb8c00' },
  OWNER_CHANGED:     { icon: 'manage_accounts',    color: '#f4511e' },
  ACTIVITY_MENTIONED:{ icon: 'record_voice_over',  color: '#8e24aa' },
  Phone_Call:        { icon: 'phone',              color: '#2196F3' },
  Email:             { icon: 'email',              color: '#4CAF50' },
  Meeting:           { icon: 'event',              color: '#FF9800' }
};

/**
 * ActivityFeedComponent
 *
 * Renders the chronological activity feed for a CRM resource.
 * Reuses the existing LeadActivityService (/api/leadActivity) so no
 * backend changes are required for basic activity display.
 *
 * Usage:
 *   <calsoft-activity-feed
 *     [leadId]="lead.entityId"
 *     [taskType]="1">
 *   </calsoft-activity-feed>
 */
@Component({
  selector: 'calsoft-activity-feed',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatIconModule,
    MatTooltipModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    CustomFlexLayoutModule
  ],
  templateUrl: './activity-feed.component.html',
  styleUrls: ['./activity-feed.component.scss']
})
export class ActivityFeedComponent implements OnInit, OnDestroy {

  @Input() leadId!: number;
  @Input() taskType = 1;

  activities: any[] = [];
  loading = false;
  page = 0;
  size = 30;
  totalPages = 1;

  private destroy$ = new Subject<void>();

  constructor(
    private leadActivityService: LeadActivityService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadActivities();
  }

  loadActivities(): void {
    if (!this.leadId) return;
    this.loading = true;
    this.leadActivityService
      .getAllLeadActivityByPageable(this.page, this.size, 'createdDate,desc', this.leadId, this.taskType)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          const content = res.content ?? res;
          this.activities = this.page === 0 ? content : [...this.activities, ...content];
          this.totalPages = res.totalPages ?? 1;
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: () => { this.loading = false; this.cdr.markForCheck(); }
      });
  }

  loadMore(): void {
    if (this.page + 1 >= this.totalPages) return;
    this.page++;
    this.loadActivities();
  }

  getActivityIcon(activity: any): string {
    const type = activity.activityType ?? activity.type ?? '';
    return ACTIVITY_CONFIG[type]?.icon ?? 'fiber_manual_record';
  }

  getActivityColor(activity: any): string {
    const type = activity.activityType ?? activity.type ?? '';
    return ACTIVITY_CONFIG[type]?.color ?? '#9E9E9E';
  }

  trackById(_: number, item: any): any {
    return item.id;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
